import React, { createContext, useContext, useMemo, useCallback, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSelector, useDispatch } from 'react-redux'
import { customReportOptions } from '../../../../features/customReport/queries/report/customReportOptions'
import { useCtxCustomReportStructure } from '../../../../features/customReport/queries/structure/useCtxCustomReportStructure'
import { contextCompanyIdSelector } from '../../../../redux/context/company/selectors'
import { activeReportIdSelector } from '../../../../redux/context/customReports/selectors'
import { setActiveReportId } from '../../../../redux/context/customReports/actions'
import { firstReport } from './utils'

interface CalculatedReportContextType {
  reportId: number | undefined
  setReportId: (id: number | undefined) => void
}

const CalculatedReportContext = createContext<CalculatedReportContextType | undefined>(undefined)

export const CalculatedReportProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch()
  const reportId = useSelector(activeReportIdSelector)
  const { data: customReportTree } = useCtxCustomReportStructure()

  const setReportId = useCallback(
    (id: number | undefined) => {
      dispatch(setActiveReportId(id))
    },
    [dispatch]
  )

  // Auto-select first report from the tree whenever no report is active
  useEffect(() => {
    if (reportId) return
    const next = firstReport(customReportTree)
    if (next?.id) {
      dispatch(setActiveReportId(next.id))
    }
  }, [reportId, customReportTree, dispatch])

  const value = useMemo(() => ({ reportId, setReportId }), [reportId, setReportId])
  return <CalculatedReportContext.Provider value={value}>{children}</CalculatedReportContext.Provider>
}

export function useCalculatedReportContext() {
  const ctx = useContext(CalculatedReportContext)
  if (!ctx) throw new Error('useCalculatedReportContext must be used within CalculatedReportProvider')
  return ctx
}

export function useCalculatedReport() {
  const { reportId } = useCalculatedReportContext()
  const companyId = useSelector(contextCompanyIdSelector) ?? ''
  const query = useQuery(customReportOptions(reportId, companyId))
  return query
}
